import React, { useState, useEffect } from 'react';
import './BulkUploadStatusModal.css';

const seasonList = ["Summer", "Winter", "Spring", "Autumn"];

const BulkUploadStatusModal = ({
  isOpen,
  onClose,
  isLoading,
  uploadedItems,
  onUpdateItem,
  onSaveAll,
  categories = [],
  subCategories = [],
  colorMapping = {},
  seasonOptions = seasonList,
  isSaving,
  setIsSaving,
  preference
}) => {
  const [editableItems, setEditableItems] = useState([]);
  
  useEffect(() => {
    if (uploadedItems) {
      setEditableItems(
        uploadedItems.map(item => {
          
          // Find current category ID by name if the stored ID doesn't exist
          let currentCategoryId = item.categoryId || item.category?._id || '';
          if (currentCategoryId && !categories.find(cat => cat._id === currentCategoryId)) {
            // ID doesn't match current categories, try to find by name
            const categoryByName = categories.find(cat => cat.name === item.category?.name);
            if (categoryByName) {
              currentCategoryId = categoryByName._id;
              console.log(`Updated category ID from ${item.categoryId} to ${currentCategoryId} based on name match`);
            }
          }
          
          // Find current subcategory ID by name if the stored ID doesn't exist
          let currentSubcategoryId = item.subcategoryId || item.subcategory?._id || '';
          if (currentSubcategoryId && !subCategories.find(sub => sub._id === currentSubcategoryId)) {
            // ID doesn't match current subcategories, try to find by name
            const subcategoryByName = subCategories.find(sub => 
              sub.name === item.subcategory?.name && 
              (preference === "Unisex" || sub.categoryId === currentCategoryId)
            );
            if (subcategoryByName) {
              currentSubcategoryId = subcategoryByName._id;
              console.log(`Updated subcategory ID from ${item.subcategoryId} to ${currentSubcategoryId} based on name match`);
            }
          }
          
          const processedItem = {
            ...item,
            isEditing: false,
            categoryId: currentCategoryId,
            subcategoryId: currentSubcategoryId
          };
          
          return processedItem;
        })
      );
    }
  }, [uploadedItems, categories, subCategories, preference]);

  // Helper to get color name from code
  const getColorName = (code) => colorMapping[code] || code;
  // Helper to get color code from name
  const getColorCode = (name) => Object.keys(colorMapping).find(key => colorMapping[key] === name) || '';

  const handleEditClick = (index) => {
    setEditableItems(prevItems =>
      prevItems.map((item, i) =>
        i === index ? { ...item, isEditing: true } : item
      )
    );
  };

  const handleSaveClick = (index) => {
    const itemToSave = editableItems[index];
  
    // Optionally attach full category/subcategory for UI
    const categoryObj = categories.find(c => c._id === itemToSave.categoryId);
    const subcategoryObj = subCategories.find(s => s._id === itemToSave.subcategoryId);
  
    const updatedItem = {
      ...itemToSave,
      category: categoryObj,
      subcategory: subcategoryObj,
    };
  
    onUpdateItem(updatedItem);
  
    setEditableItems(prevItems =>
      prevItems.map((item, i) =>
        i === index ? { ...updatedItem, isEditing: false } : item
      )
    );
  };
  

  const handleChange = (e, index, field) => {
    const { value } = e.target;
    setEditableItems(prevItems =>
      prevItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  // For color name dropdown: update both name and code
  const handleColorNameChange = (e, index, fieldName, codeField) => {
    const colorName = e.target.value;
    const colorCode = getColorCode(colorName);
    setEditableItems(prevItems =>
      prevItems.map((item, i) =>
        i === index
          ? { ...item, [fieldName]: colorName, [codeField]: colorCode }
          : item
      )
    );
  };

  const handleMultiSelectChange = (e, index, field) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setEditableItems(prevItems =>
      prevItems.map((item, i) =>
        i === index ? { ...item, [field]: options } : item
      )
    );
  };

  const handleSaveAll = () => {
    onSaveAll(editableItems); // Call the parent save all function
    // onClose(); 
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="bulk-upload-modal-overlay" style={{zIndex:"9999"}} >
      <div className="bulk-upload-modal-content" style={{height:"650px",overflowY:"auto",position:"relative",zIndex:"9999"}}>
        <div className="modal-header">
          <h2>Bulk Upload Review</h2>
          <span className="close" onClick={onClose}>
            &times;
          </span>
        </div>
        {isLoading ? (
          <div className="skeleton-loader-container">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="skeleton-row">
                <div className="skeleton-col skeleton-image"></div>
                <div className="skeleton-col skeleton-text"></div>
                <div className="skeleton-col skeleton-text"></div>
                <div className="skeleton-col skeleton-text"></div>
                <div className="skeleton-col skeleton-text"></div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            {editableItems.length > 0 ? (
              <div className="table-container-modal" style={{height:"520px",overflowY:"scroll"}}>
                <table className="bulk-upload-table improved-ui">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Sub Category</th>
                      <th>Brand</th>
                      <th>Is Retired</th>
                      <th>Primary Color Name</th>
                      <th>Primary Color Code</th>
                      <th>Secondary Color Name</th>
                      <th>Secondary Color Code</th>
                      <th>Size</th>
                      <th>Date Of Purchase</th>
                      <th>Price</th>
                      <th>Print Style</th>
                      <th>Texture Style</th>
                      <th>Collar Style</th>
                      <th>Sleeve Length</th>
                      <th>Instructions</th>
                      <th>Tags</th>
                      <th>State</th>
                      <th>Wearing Season</th>
                      <th>Created At</th>
                      <th>Updated At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editableItems.map((item, index) => {
                      console.log("item",item)
                    return(
                      <tr key={item._id || index}>
                        {/* Image (not editable) */}
                        <td>
                          <img src={item.image} alt={item.name} className="wardrobe-image-small" />
                        </td>
                        {/* Name */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.name || ''} onChange={(e) => handleChange(e, index, 'name')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.name
                          )}
                        </td>
                        {/* Description */}
                        <td>
                          {item.isEditing ? (
                            <textarea value={item.description || ''} onChange={(e) => handleChange(e, index, 'description')} style={{minWidth:"100px"}}/>
                          ) : (
                            item.description
                          )}
                        </td>
                        {/* Category Dropdown */}
                        <td>
                          {item.isEditing ? (
                            <select
                              value={item.categoryId || ''}
                              onChange={(e) => handleChange(e, index, 'categoryId')}
                              style={{ minWidth: "100px", padding: "10px" }}
                            >
                              <option value="">Select Category</option>
                              {categories.map((cat) => (
                                <option key={cat._id} value={cat._id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            item.category?.name || item.categoryId
                          )}
                        </td>

                        {/* Subcategory Dropdown - Filtered by Selected Category */}
                        <td>
                          {item.isEditing ? (
                            <select
                              value={item.subcategoryId || ''}
                              onChange={(e) => handleChange(e, index, 'subcategoryId')}
                              style={{ minWidth: "100px", padding: "10px" }}
                            >
                              <option value="">Select Sub Category</option>
                              {subCategories
                                .filter((sub) => {
                                  if(preference==="Unisex") return true
                                  return sub.categoryId === item.categoryId;
                                })
                                .map((sub) => (
                                  <option key={sub._id} value={sub._id}>
                                    {sub.name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            item.subcategory?.name  || item.subcategoryId
                          )}
                        </td>

                        {/* Brand */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.brand || ''} onChange={(e) => handleChange(e, index, 'brand')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.brand
                          )}
                        </td>
                        {/* Is Retired */}
                        <td>
                          {item.isEditing ? (
                            <select value={item.isRetired ? 'Yes' : 'No'} onChange={(e) => handleChange(e, index, 'isRetired')} style={{minWidth:"100px",padding:"10px"}}>
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          ) : (
                            item.isRetired ? 'Yes' : 'No'
                          )}
                        </td>
                        {/* Primary Color Name (dropdown) */}
                        <td>
                          {item.isEditing ? (
                            <select value={item.primaryColorName || ''} onChange={(e) => handleColorNameChange(e, index, 'primaryColorName', 'primaryColorCode')} style={{minWidth:"100px",padding:"10px"}}>
                              <option value={""}>Select Color</option>
                              {Object.values(colorMapping).map(color => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                            </select>
                          ) : (
                            item.primaryColorName
                          )}
                        </td>
                        {/* Primary Color Code (input, auto-updates with name) */}
                        <td>
                          {
                            item.primaryColorCode
                          }
                        </td>
                        {/* Secondary Color Name (dropdown) */}
                        <td>
                          {item.isEditing ? (
                            <select value={item.secondaryColorName || ''} onChange={(e) => handleColorNameChange(e, index, 'secondaryColorName', 'secondaryColorCode')} style={{minWidth:"100px",padding:"10px"}}>
                              <option value={""}>Select Color</option>
                              {Object.values(colorMapping).map(color => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                            </select>
                          ) : (
                            item.secondaryColorName
                          )}
                        </td>
                        {/* Secondary Color Code (input, auto-updates with name) */}
                        <td>
                          { 
                            item.secondaryColorCode
                          }
                        </td>
                        {/* Size */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.size || ''} onChange={(e) => handleChange(e, index, 'size')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.size
                          )}
                        </td>
                        {/* Date Of Purchase (not editable) */}
                        <td>
                          {item.dateOfPurchase}
                        </td>
                        {/* Price */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.price || ''} onChange={(e) => handleChange(e, index, 'price')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.price
                          )}
                        </td>
                        {/* Print Style */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.printStyle || ''} onChange={(e) => handleChange(e, index, 'printStyle')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.printStyle
                          )}
                        </td>
                        {/* Texture Style */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.textureStyle || ''} onChange={(e) => handleChange(e, index, 'textureStyle')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.textureStyle
                          )}
                        </td>
                        {/* Collar Style */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.collarStyle || ''} onChange={(e) => handleChange(e, index, 'collarStyle')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.collarStyle
                          )}
                        </td>
                        {/* Sleeve Length */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.sleeveLength || ''} onChange={(e) => handleChange(e, index, 'sleeveLength')} style={{minWidth:"100px",padding:"10px"}} />
                          ) : (
                            item.sleeveLength
                          )}
                        </td>
                        {/* Instructions */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={Array.isArray(item.instructions) ? item.instructions.join(', ') : (item.instructions || '')} onChange={(e) => handleChange(e, index, 'instructions')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            Array.isArray(item.instructions) ? item.instructions.join(', ') : item.instructions
                          )}
                        </td>
                        {/* Tags */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || '')} onChange={(e) => handleChange(e, index, 'tags')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            Array.isArray(item.tags) ? item.tags.join(', ') : item.tags
                          )}
                        </td>
                        {/* State */}
                        <td>
                          {item.isEditing ? (
                            <input type="text" value={item.state || ''} onChange={(e) => handleChange(e, index, 'state')} style={{minWidth:"100px",padding:"10px"}}/>
                          ) : (
                            item.state
                          )}
                        </td>
                        {/* Wearing Season (dropdown, multi-select) */}
                        <td>
                          {item.isEditing ? (
                            <select multiple value={item.wearingSeason || []} onChange={(e) => handleMultiSelectChange(e, index, 'wearingSeason')} style={{minWidth:"100px",padding:"10px"}}>
                              {seasonOptions.map(season => (
                                <option key={season} value={season}>{season}</option>
                              ))}
                            </select>
                          ) : (
                            Array.isArray(item.wearingSeason) ? item.wearingSeason.join(', ') : item.wearingSeason
                          )}
                        </td>
                        {/* Created At (not editable) */}
                        <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</td>
                        {/* Updated At (not editable) */}
                        <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : ''}</td>
                        {/* Actions */}
                        <td>
                          {item.isEditing ? (
                            <button onClick={() => handleSaveClick(index)} className="save-button">Save</button>
                          ) : (
                            <button onClick={() => handleEditClick(index)} className="edit-button">Edit</button>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
                
              </div>
            ) : (
              <p>No items uploaded or found.</p>
            )}
          </div>
        )}
        <div className="modal-actions" style={{position:"absolute",bottom:0,left:0,right:0,backgroundColor:"white",padding:"10px",borderTop:"1px solid #ccc"}}>
        <button
        onClick={handleSaveAll}
        className="submit-button"
        disabled={isSaving||isLoading}
        style={{
          opacity: (isSaving||isLoading) ? 0.6 : 1,
          cursor: (isSaving||isLoading) ? 'not-allowed' : 'pointer',
        }}
      >
        {(isSaving) ? 'Saving...' : isLoading ? 'Wait' : 'Save All'}
      </button>
                  <button disabled={isLoading} onClick={onClose} className="cancel-button"
                   style={{
                    opacity: (isLoading) ? 0.6 : 1,
                    cursor: (isLoading) ? 'not-allowed' : 'pointer',
                  }}
                  >Close</button>
                </div>
      </div>
    </div>
  );
};

export default BulkUploadStatusModal;